import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getCerts } from "../features/managedServices/services/managedServiceApi";
import type { CertListItem, MsFilters } from "../features/managedServices/types";
import { AppDispatch, RootState } from "./types";

export interface MsCertsState {
    isLoading: boolean;
    error: string | null;
    items: CertListItem[];
    total: number;
    page: number;
    pageSize: number;
    // [columnId, ascending]. For evaluatedAt, ascending = youngest/newest first
    // (API evaluated_at desc). Default is ascending Age.
    sortColumn: [string, boolean];
}

const DEFAULT_PAGE_SIZE = 20;

const initialState: MsCertsState = {
    isLoading: false,
    error: null,
    items: [],
    total: 0,
    page: 1,
    // Fallback only — callers seed from session.rowsPerPage on first load.
    pageSize: DEFAULT_PAGE_SIZE,
    sortColumn: ["evaluatedAt", true],
};

interface CertsPagePayload {
    items: CertListItem[];
    total: number;
    page: number;
    pageSize: number;
}

const msCertsSlice = createSlice({
    name: "managedServiceCerts",
    initialState,
    reducers: {
        setLoading: (state: MsCertsState, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state: MsCertsState, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setPage: (state: MsCertsState, action: PayloadAction<CertsPagePayload>) => {
            state.items = action.payload.items;
            state.total = action.payload.total;
            state.page = action.payload.page;
            state.pageSize = action.payload.pageSize;
        },
        setSortColumn: (state: MsCertsState, action: PayloadAction<[string, boolean]>) => {
            state.sortColumn = action.payload;
        },
    },
});

export const { setLoading, setError, setPage, setSortColumn } = msCertsSlice.actions;

export const selectMsCerts = (state: RootState): MsCertsState => state.managedServiceCerts;

export const loadCerts =
    (filters: MsFilters, verdicts: string[], page: number, pageSize: number) =>
    async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
        dispatch(setLoading(true));
        dispatch(setError(null));
        try {
            const { sortColumn } = getState().managedServiceCerts;
            const result = await getCerts(filters, verdicts, page, pageSize, sortColumn);
            dispatch(
                setPage({
                    items: result.items,
                    total: result.total,
                    page: result.page,
                    pageSize: result.pageSize,
                })
            );
        } catch (error) {
            dispatch(setError("Unable to load certificates. Please try again."));
            // eslint-disable-next-line no-console
            console.error("loadCerts failed:", error);
        } finally {
            dispatch(setLoading(false));
        }
    };

export default msCertsSlice.reducer;
